import React from 'react'
import { Dialog } from '@mui/material'
import {
  Close as CloseIcon, ContentCopy, PictureAsPdfOutlined, OpenInNew, Block as DeactivateIcon, QrCode2,
  UploadOutlined, CheckCircleOutline,
} from '@mui/icons-material'
import { useDispatch } from '../../store'
import { createQrRegistration } from '../../store/qrRegistration/actionCreator'
import { QrRegistrationResult } from '../../store/qrRegistration/actionType'
import { downloadBlob } from '../../store/support/service'
import { formatDate } from '../../function/date'
import defaultBackgroundUrl from '../../asset/img/qr_code_background.jpg'
import { QrPdfView, QrPosterMock } from './QrPoster'
import { fetchQrPdf, qrStatus, qrWhen, STATUS_COLOR, STATUS_LABEL, validityLine } from './qrRegistration'
import l from '../../page/SubscribersPageV2.module.css'
import s from './QrRegistrationV2.module.css'

/*
 * The v2 page's dialogs. Preview: the printed poster as the backend renders
 * it (the real PDF), with the link and its actions. Create: the classic
 * popup's fields and rules (PopupCreateQrRegistration — label, an optional
 * validity window, a PNG/JPG background up to 4 MB, the bundled background
 * when none is picked) beside a live mock of the poster; once created, the
 * real poster, the link and the PDF.
 */

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']
const MAX_IMAGE_SIZE = 1024 * 1024 * 4

async function defaultBackgroundFile(): Promise<File> {
  const response = await fetch(defaultBackgroundUrl)
  const blob = await response.blob()
  return new File([blob], 'qr_code_background.jpg', { type: blob.type || 'image/jpeg' })
}

/** "qr-registration-lobby-poster.pdf" — the label when there is one */
export const qrPdfName = (row: { id: number; label?: string }) =>
  `qr-registration-${row.label ? row.label.trim().replace(/[^\w-]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || row.id : row.id}.pdf`

function Shell({ theme, title, sub, onClose, children, wide }: {
  theme: string; title: React.ReactNode; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode; wide?: boolean
}) {
  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, maxHeight: 'calc(100vh - 32px)' } }}>
      <div className={`${l.theme} ${s.shell} ${wide ? s.shellWide : ''}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.topIcon}><QrCode2 sx={{ fontSize: 20 }} /></span>
          <div className={s.topText}><b>{title}</b>{sub && <span>{sub}</span>}</div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>
        {children}
      </div>
    </Dialog>
  )
}

export function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <div className={s.link}>
      <code>{url}</code>
      <button
        type="button"
        className={s.ghostSm}
        onClick={() => { void navigator.clipboard?.writeText(url).catch(() => undefined); setCopied(true); window.setTimeout(() => setCopied(false), 1300) }}
      >
        <ContentCopy sx={{ fontSize: 14 }} />{copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/** the PDF already shown, or a fresh one when it is not there yet */
async function savePdf(row: QrRegistrationResult, shown: Blob | null) {
  downloadBlob(shown || await fetchQrPdf(row.id), qrPdfName(row))
}

export function QrPreviewDialogV2({ row, theme, onClose, onDeactivate }: {
  row: QrRegistrationResult; theme: string; onClose: () => void; onDeactivate: (row: QrRegistrationResult) => void
}) {
  const [blob, setBlob] = React.useState<Blob | null>(null)
  const [saving, setSaving] = React.useState(false)
  const st = qrStatus(row)
  const when = qrWhen(row)
  return (
    <Shell theme={theme} title={row.label || `QR link #${row.id}`} sub="The printed poster — as the PDF comes out" onClose={onClose} wide>
      <div className={s.split}>
        <div className={s.pdfPane}><QrPdfView id={row.id} onBlob={setBlob} /></div>
        <aside className={s.side}>
          <div className={s.fact}>
            <small>Status</small>
            <span className={l.status}><span className={l.dot} style={{ background: STATUS_COLOR[st] }} />{STATUS_LABEL[st]}</span>
            {when && <span className={s.muted}>{when}</span>}
          </div>
          <div className={s.fact}>
            <small>Validity</small>
            <span>{validityLine(row.validFrom, row.validUntil) || 'No limits — works until deactivated'}</span>
          </div>
          <div className={s.fact}>
            <small>Join link — what the QR opens</small>
            <CopyLink url={row.joinUrl} />
          </div>
          <div className={s.fact}>
            <small>Created</small>
            <span>{formatDate(row.createdTime, 'dd.MM.yyyy HH:mm')}</span>
          </div>
          <div className={s.fact}>
            <small>Background</small>
            <span>{row.hasBackgroundImage ? 'Uploaded image' : 'Brand gradient'}</span>
          </div>
          <div className={s.sideBtns}>
            <button
              type="button"
              className={s.primary}
              disabled={saving}
              onClick={async () => { setSaving(true); try { await savePdf(row, blob) } finally { setSaving(false) } }}
            >
              <PictureAsPdfOutlined sx={{ fontSize: 17 }} />{saving ? 'Preparing…' : 'Download PDF'}
            </button>
            <button type="button" className={s.ghost} onClick={() => window.open(row.joinUrl, '_blank', 'noopener,noreferrer')}>
              <OpenInNew sx={{ fontSize: 16 }} />Open the signup page
            </button>
            {row.isActive && (
              <button type="button" className={`${s.ghost} ${s.danger}`} onClick={() => onDeactivate(row)}>
                <DeactivateIcon sx={{ fontSize: 16 }} />Deactivate
              </button>
            )}
          </div>
          {!row.isActive && <p className={s.note}>Deactivated: scanning a printed copy shows that the link no longer works.</p>}
        </aside>
      </div>
    </Shell>
  )
}

export function QrCreateDialogV2({ theme, onClose, onCreated }: { theme: string; onClose: () => void; onCreated: () => void }) {
  const dispatch = useDispatch()
  const uploadRef = React.useRef<HTMLInputElement>(null)
  const [label, setLabel] = React.useState('')
  const [validFrom, setValidFrom] = React.useState('')
  const [validUntil, setValidUntil] = React.useState('')
  const [image, setImage] = React.useState<File | null>(null)
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)
  const [imageError, setImageError] = React.useState('')
  const [rangeError, setRangeError] = React.useState('')
  const [creating, setCreating] = React.useState(false)
  const [result, setResult] = React.useState<QrRegistrationResult | null>(null)
  const [blob, setBlob] = React.useState<Blob | null>(null)
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl) }, [imageUrl])
  React.useEffect(() => { setRangeError('') }, [validFrom, validUntil])

  function pick(e: React.ChangeEvent<HTMLInputElement>) {
    setImageError('')
    const file = e.target.files?.[0]
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    if (!IMAGE_EXTENSIONS.includes(ext)) { setImageError('PNG or JPG only'); return }
    if (file.size > MAX_IMAGE_SIZE) { setImageError('Up to 4 MB'); return }
    setImage(file)
    setImageUrl(URL.createObjectURL(file))
  }
  function clearImage() {
    if (uploadRef.current) uploadRef.current.value = ''
    setImage(null)
    setImageUrl(null)
    setImageError('')
  }

  async function create() {
    if (validFrom && validUntil && new Date(validUntil) <= new Date(validFrom)) { setRangeError('"Until" must be after "From"'); return }
    setCreating(true)
    // no image picked → the bundled one, as the classic popup does
    let file: File | undefined = image || undefined
    if (!file) {
      try { file = await defaultBackgroundFile() } catch { file = undefined }
    }
    dispatch(createQrRegistration(
      {
        label: label.trim() || undefined,
        validFrom: validFrom ? new Date(validFrom).toISOString() : undefined,
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        file,
      },
      (data) => { setCreating(false); setResult(data); onCreated() },
      () => setCreating(false),
    ))
  }

  if (result) {
    return (
      <Shell theme={theme} title="QR link created" sub={result.label || `QR link #${result.id}`} onClose={onClose} wide>
        <div className={s.split}>
          <div className={s.pdfPane}><QrPdfView id={result.id} onBlob={setBlob} /></div>
          <aside className={s.side}>
            <div className={s.done}><CheckCircleOutline sx={{ fontSize: 20 }} />Ready to print. People who scan it land on the phone-only signup.</div>
            <div className={s.fact}>
              <small>Join link — what the QR opens</small>
              <CopyLink url={result.joinUrl} />
            </div>
            <div className={s.sideBtns}>
              <button
                type="button"
                className={s.primary}
                disabled={saving}
                onClick={async () => { setSaving(true); try { await savePdf(result, blob) } finally { setSaving(false) } }}
              >
                <PictureAsPdfOutlined sx={{ fontSize: 17 }} />{saving ? 'Preparing…' : 'Download PDF'}
              </button>
              <button type="button" className={s.ghost} onClick={onClose}>Done</button>
            </div>
          </aside>
        </div>
      </Shell>
    )
  }

  const validity = validityLine(validFrom ? new Date(validFrom).toISOString() : null, validUntil ? new Date(validUntil).toISOString() : null)
  return (
    <Shell theme={theme} title="New QR registration link" sub="A printable QR that opens the phone-only signup" onClose={creating ? () => undefined : onClose} wide>
      <div className={s.split}>
        <div className={s.form}>
          <label className={s.field}>
            <span className={s.label}>Label <em>only admins see it — e.g. “Lobby poster”, “Conference booth”</em></span>
            <input className={s.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Lobby poster" maxLength={120} />
          </label>
          <div className={s.grid2}>
            <label className={s.field}>
              <span className={s.label}>Valid from <em>optional</em></span>
              <input className={s.input} type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </label>
            <label className={s.field}>
              <span className={s.label}>Valid until <em>optional</em></span>
              <input className={s.input} type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </label>
          </div>
          {rangeError ? <span className={s.err}>{rangeError}</span> : <span className={s.hint}>Leave both empty for a link that works until you deactivate it.</span>}
          <div className={s.field}>
            <span className={s.label}>Poster background <em>PNG or JPG, up to 4 MB</em></span>
            <div className={s.upload}>
              <span className={s.uploadThumb} style={{ backgroundImage: `url(${imageUrl || defaultBackgroundUrl})` }} />
              <div className={s.uploadText}>
                <b>{image ? image.name : 'Default background'}</b>
                <span>{image ? `${(image.size / 1024 / 1024).toFixed(1)} MB · darkened under the text` : 'Used when you pick nothing'}</span>
              </div>
              <button type="button" className={s.ghostSm} onClick={() => uploadRef.current?.click()}><UploadOutlined sx={{ fontSize: 15 }} />{image ? 'Change' : 'Upload'}</button>
              {image && <button type="button" className={s.ghostSm} onClick={clearImage} aria-label="Remove the image"><CloseIcon sx={{ fontSize: 15 }} /></button>}
              <input ref={uploadRef} type="file" accept=".png,.jpg,.jpeg" hidden onChange={pick} />
            </div>
            {imageError && <span className={s.err}>{imageError}</span>}
          </div>
        </div>
        <aside className={s.mockPane}>
          <span className={s.mockHead}>Poster preview</span>
          <QrPosterMock background={imageUrl || defaultBackgroundUrl} label={label.trim()} validity={validity} />
        </aside>
      </div>
      <div className={s.foot}>
        <span className={s.footNote}>The link works as soon as it is created{validFrom ? ' — from the start date' : ''}.</span>
        <button type="button" className={s.ghost} onClick={onClose} disabled={creating}>Cancel</button>
        <button type="button" className={s.primary} onClick={() => { void create() }} disabled={creating}>
          <QrCode2 sx={{ fontSize: 17 }} />{creating ? 'Creating…' : 'Create link'}
        </button>
      </div>
    </Shell>
  )
}
